import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import '../styles/components.css';

// Un composant pour afficher les messages à l'utilisateur
const MessageBox = ({ message, type, onClose }) => {
    if (!message) return null;

    return (
        <div className={`message-box ${type}`}>
            <div className="message-content">
                <span>{message}</span>
                <button onClick={onClose} className="message-close-button">&times;</button>
            </div>
        </div>
    );
};

// --------------------------------------------------------------------------------
// MODIFICATION CLÉ : Ajout de 'promoCode' à la déstructuration des props
// --------------------------------------------------------------------------------
export default function CheckoutForm({ email, firstName, lastName, userId, customerId, context, onPaymentSuccess, cartItems, promoCode }) {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [message, setMessage] = useState({ text: null, type: null });
    
    // NOTE : L'ancien état promotionCode a été supprimé, car la valeur est maintenant reçue via les props.

    // Initialisation de l'état du formulaire avec les props reçues
    const [formData, setFormData] = useState({
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
    });

    // Mettre à jour l'état du formulaire si les props changent
    useEffect(() => {
        setFormData({
            firstName: firstName || '',
            lastName: lastName || '',
            email: email || '',
        });
    }, [firstName, lastName, email]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
    };

    const handlePayment = async (e) => {
        e.preventDefault();
        setMessage({ text: null, type: null });

        const API_URL = process.env.REACT_APP_API_URL;

        if (!stripe || !elements || !cardComplete) {
            setMessage({ text: "Veuillez remplir toutes les informations de carte.", type: 'error' });
            return;
        }

        // Vérification des données du formulaire
        if (!formData.firstName || !formData.lastName || !formData.email) {
            setMessage({ text: "Veuillez vérifier vos informations de facturation.", type: 'error' });
            return;
        }

        setLoading(true);

        try {
            // Création du PaymentMethod
            const cardElement = elements.getElement(CardElement);
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    email: formData.email,
                    name: `${formData.firstName} ${formData.lastName}`,
                },
            });

            if (pmError) {
                setLoading(false);
                setMessage({ text: pmError.message, type: 'error' });
                return;
            }

            // Création du PaymentIntent côté back-end avec les articles du panier
            const intentRes = await fetch(`${API_URL}/payment/create-payment-intent.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    userId,
                    customerId,
                    paymentMethodId: paymentMethod.id,
                    // Passage des articles du panier au backend
                    items: cartItems.map(item => ({ id: item.id, stripe_price_id: item.stripe_price_id, quantity: item.quantity })),
                    // --------------------------------------------------------------------------------
                    // MODIFICATION CLÉ : Passage du code promo reçu en prop au backend
                    // --------------------------------------------------------------------------------
                    promotionCode: promoCode, 
                }),
            });
            const intentData = await intentRes.json();
            if (intentData.error) {
                throw new Error(intentData.error);
            }

            const { clientSecret } = intentData;

            // Confirmer le paiement avec Stripe
            const confirmResult = await stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethod.id,
            });

            if (confirmResult.error) {
                setMessage({ text: confirmResult.error.message, type: 'error' });
                return;
            }

            const status = confirmResult.paymentIntent?.status;

            if (status === 'succeeded') {
                setMessage({ text: 'Paiement réussi !', type: 'success' });
                
                if (context === 'checkout') {
                    if (onPaymentSuccess) onPaymentSuccess();
                    navigate('/dashboard');
                } else {
                    navigate('/dashboard');
                }
                return;
            } else {
                setMessage({ text: 'Paiement non réussi, statut : ' + status, type: 'error' });
            }

        } catch (err) {
            setMessage({ text: 'Erreur : ' + err.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const isFormComplete = formData.email && formData.firstName && formData.lastName && cardComplete;

    return (
        <form onSubmit={handlePayment} className="checkout-form">
            {message.text && <MessageBox message={message.text} type={message.type} onClose={() => setMessage({ text: null, type: null })} />}
            
            <div className="form-group">
                <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Entrez votre prénom"
                    required
                    style={{ display: 'none' }}
                />
            </div>
            
            <div className="form-group">
                <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Entrez votre nom"
                    required
                    style={{ display: 'none' }}
                />
            </div>

            <div className="form-group disabled">
                <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Entrez votre email"
                    disabled
                    required
                    style={{ display: 'none' }}
                />
            </div>

            {/* Le champ d'input Code promotionnel a été retiré, car il est géré dans le composant parent Checkout.js */}

            <div className="card-element-container">
                <CardElement
                    options={{ hidePostalCode: true }}
                    onChange={(event) => setCardComplete(event.complete)}
                />
            </div>
            
            <button
                type="submit"
                disabled={!stripe || loading || !isFormComplete}
                className="submit-button"
            >
                {loading ? 'Paiement en cours...' : `Payer`}
            </button>
        </form>
    );
}

// --------------------------------------------------------------------------------
// MODIFICATION CLÉ : Mise à jour des PropTypes pour inclure promoCode
// --------------------------------------------------------------------------------
CheckoutForm.propTypes = {
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    userId: PropTypes.string,
    customerId: PropTypes.string,
    context: PropTypes.string,
    onPaymentSuccess: PropTypes.func,
    cartItems: PropTypes.array.isRequired,
    promoCode: PropTypes.string, // La nouvelle prop est une chaîne de caractères (non requise)
};